import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface DuplicateGroup {
  key: string;
  ids: string[];
  vendors: any[];
  reason: string;
}

function normalize(s: string | null | undefined): string {
  if (!s) return "";
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .replace(/\b(llc|inc|corp|co|ltd|company|group|enterprises?|services?|construction|contracting|supply|supplies)\b/g, "")
    .trim();
}

function normalizePhone(p: string | null | undefined): string {
  if (!p) return "";
  return p.replace(/\D/g, "").slice(-10);
}

function normalizeEmail(e: string | null | undefined): string {
  if (!e) return "";
  return e.toLowerCase().trim();
}

function normalizeEin(e: string | null | undefined): string {
  if (!e) return "";
  return e.replace(/\D/g, "");
}

function detectDuplicates(vendors: any[]): DuplicateGroup[] {
  const nameMap = new Map<string, any[]>();
  const emailMap = new Map<string, any[]>();
  const phoneMap = new Map<string, any[]>();
  const einMap = new Map<string, any[]>();

  for (const v of vendors) {
    const nName = normalize(v.name);
    const nEmail = normalizeEmail(v.email);
    const nPhone = normalizePhone(v.phone);
    const nEin = normalizeEin(v.ein);

    if (nName) {
      if (!nameMap.has(nName)) nameMap.set(nName, []);
      nameMap.get(nName)!.push(v);
    }
    if (nEmail) {
      if (!emailMap.has(nEmail)) emailMap.set(nEmail, []);
      emailMap.get(nEmail)!.push(v);
    }
    if (nPhone && nPhone.length >= 7) {
      if (!phoneMap.has(nPhone)) phoneMap.set(nPhone, []);
      phoneMap.get(nPhone)!.push(v);
    }
    if (nEin && nEin.length >= 9) {
      if (!einMap.has(nEin)) einMap.set(nEin, []);
      einMap.get(nEin)!.push(v);
    }
  }

  const parent = new Map<string, string>();
  const find = (id: string): string => {
    if (!parent.has(id)) parent.set(id, id);
    if (parent.get(id) !== id) parent.set(id, find(parent.get(id)!));
    return parent.get(id)!;
  };
  const union = (a: string, b: string) => {
    const ra = find(a), rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };

  const reasonMap = new Map<string, Set<string>>();
  const addReason = (ids: string[], reason: string) => {
    for (const id of ids) {
      if (!reasonMap.has(id)) reasonMap.set(id, new Set());
      reasonMap.get(id)!.add(reason);
    }
  };

  for (const [, group] of nameMap) {
    if (group.length < 2) continue;
    const ids = group.map((v: any) => v.id);
    addReason(ids, "Same name");
    for (let i = 1; i < ids.length; i++) union(ids[0], ids[i]);
  }
  for (const [email, group] of emailMap) {
    if (group.length < 2) continue;
    const ids = group.map((v: any) => v.id);
    addReason(ids, `Same email (${email})`);
    for (let i = 1; i < ids.length; i++) union(ids[0], ids[i]);
  }
  for (const [, group] of phoneMap) {
    if (group.length < 2) continue;
    const ids = group.map((v: any) => v.id);
    addReason(ids, "Same phone");
    for (let i = 1; i < ids.length; i++) union(ids[0], ids[i]);
  }
  for (const [, group] of einMap) {
    if (group.length < 2) continue;
    const ids = group.map((v: any) => v.id);
    addReason(ids, "Same EIN");
    for (let i = 1; i < ids.length; i++) union(ids[0], ids[i]);
  }

  const groups = new Map<string, any[]>();
  for (const v of vendors) {
    const root = find(v.id);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root)!.push(v);
  }

  const result: DuplicateGroup[] = [];
  for (const [, members] of groups) {
    if (members.length < 2) continue;
    const reasons = new Set<string>();
    for (const m of members) {
      for (const r of reasonMap.get(m.id) ?? []) reasons.add(r);
    }
    result.push({
      key: members[0].id,
      ids: members.map((v: any) => v.id),
      vendors: members,
      reason: Array.from(reasons).join(", "),
    });
  }

  return result.sort((a, b) => b.vendors.length - a.vendors.length);
}

export function useVendorDuplicates() {
  const { user } = useAuth();

  const { data: allVendors, isLoading } = useQuery({
    queryKey: ["vendors_all_for_duplicates"],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from("vendors") as any)
        .select("*")
        .eq("is_deleted", false)
        .order("name");
      if (error) throw error;
      return (data ?? []) as any[];
    },
    enabled: !!user,
    staleTime: 60_000,
  });

  const { groups, duplicateIds } = useMemo(() => {
    if (!allVendors?.length) return { groups: [] as DuplicateGroup[], duplicateIds: new Set<string>() };
    const g = detectDuplicates(allVendors);
    const ids = new Set<string>();
    for (const group of g) {
      for (const id of group.ids) ids.add(id);
    }
    return { groups: g, duplicateIds: ids };
  }, [allVendors]);

  return { groups, duplicateIds, isLoading, allVendors: allVendors ?? [] };
}
